import React, { useContext, useState } from 'react';
import { FiX } from 'react-icons/fi';
import classNames from 'classnames';
import LocalesSvg from 'public/images/svg_icons/locales.svg';
import TranslationsSvg from 'public/images/svg_icons/translations.svg';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import { Modal } from 'react-bootstrap';
import useInitialData from 'src/hooks/useInitialData';
import { sortBy } from 'lodash-es';
import currencies from 'src/data/currencies';
import Cookies from 'js-cookie';
import { CurrencySelectorContext } from 'src/contexts/CurrencySelectorProvider';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { SUPPORTED_LOCALES } from 'src/lib/constants';

const LocaleSelector: React.FC<{ onClick?: () => void; showLanguageName?: boolean }> = ({
  showLanguageName,
  onClick,
}) => {
  const translation = useTranslations('components.LocaleSelector');
  const currentCurrency = useCurrentCurrency();
  const initialData = useInitialData();
  const orderedCurrencies = sortBy(
    currencies.filter((o) => initialData.checkoutSupportedCurrencies.includes(o.code)),
    [(o) => o.fullName]
  );
  const router = useRouter();
  const { isDisabled } = useContext(CurrencySelectorContext);
  const [show, setShow] = useState(false);
  const [tab, setTab] = useState<string>('currency');
  const [currency, setCurrency] = useState<string>(currentCurrency);
  const { locale } = router;
  const [selectedLocale, setSelectedLocale] = useState(locale);

  return (
    <>
      <div
        className="tw-flex tw-items-center tw-cursor-pointer"
        onClick={() => {
          if (typeof onClick === 'function') {
            onClick();
          }
          setTimeout(() => {
            setShow(true);
          }, 100);
        }}
      >
        <div className="tw-flex tw-items-center">
          <div>
            <LocalesSvg className="tw-align-top" width={20} height={20} />
          </div>
          {showLanguageName ? (
            <div className="tw-font-styleSans tw-font-semibold tw-ml-2">
              {translation('languageName')}
            </div>
          ) : null}
        </div>
        <div className="tw-border-l tw-border-solid tw-border-current tw-h-4 tw-mx-2"></div>
        <div className="tw-font-styleSans tw-font-semibold">{currentCurrency}</div>
      </div>

      <>
        <Modal
          show={show}
          id="modal-select-langague-currency"
          contentClassName="md:tw-rounded-lg"
          dialogClassName="modal-dialog-centered modal-full-width--sp modal-dialog-scrollable modal-lg"
          onHide={() => {
            setShow(false);
          }}
        >
          <Modal.Header>
            <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
              <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
                {translation('regionalSettings')}
              </div>
              <FiX
                size={24}
                className="tw-cursor-pointer"
                onClick={() => {
                  setShow(false);
                }}
              />
            </div>
          </Modal.Header>
          <Modal.Body>
            <div>
              <div className="list-horizontal-scroll tw-justify-start">
                <div
                  onClick={() => {
                    setTab('currency');
                  }}
                  className={classNames(
                    'tw-px-3 tw-pb-2 tw-cursor-pointer tw-text-lg md:tw-text-xl tw-border-orange tw-inline-block tw-font-semibold tw-font-styleSans tw-text-brand-navy',
                    { 'tw-border-b-2 tw-text-brand-navy': tab === 'currency' }
                  )}
                >
                  {translation('currency')}
                </div>
                <div
                  onClick={() => {
                    setTab('language');
                  }}
                  className={classNames(
                    'tw-px-3 tw-pb-2 tw-cursor-pointer tw-whitespace-nowrap tw-text-lg md:tw-text-xl tw-border-orange tw-inline-block tw-font-semibold tw-font-styleSans tw-text-brand-navy',
                    { 'tw-border-b-2 tw-text-brand-navy': tab === 'language' }
                  )}
                >
                  {translation('language')}
                </div>
              </div>
              <div className="tw-border-t tw-border-grey-lighter md:tw-mb-6 tw-mb-5"></div>
              {tab === 'language' ? (
                <div>
                  <div className="tw-font-semibold tw-mb-5">{translation('chooseALanaguage')}</div>
                  <div className="row tw-mb-4">
                    {SUPPORTED_LOCALES.map((sLocale, index) => {
                      return (
                        <div className="col-md-3 col-6 tw-mb-4" key={sLocale}>
                          <div
                            onMouseLeave={() => {
                              setSelectedLocale(locale);
                            }}
                            onMouseEnter={() => {
                              setSelectedLocale(sLocale);
                            }}
                            onClick={() => {
                              if (sLocale === 'en') {
                                Cookies.remove('NEXT_LOCALE', {
                                  path: '/',
                                  domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
                                });
                                window.location.href = router.asPath
                              } else {
                                Cookies.set('NEXT_LOCALE', sLocale, {
                                  path: '/',
                                  domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
                                });
                                window.location.href = `/${sLocale}${router.asPath}`
                              }
                              setShow(false);
                            }}
                            className={classNames(
                              'tw-px-3 tw-py-3 tw-cursor-pointer tw-rounded-lg',
                              {
                                ' tw-bg-white tw-border-brand-navy tw-border-solid tw-border':
                                  sLocale === locale || (sLocale === 'en' && locale === 'default'),
                                'hover:tw-bg-white': sLocale !== locale,
                              }
                            )}
                          >
                            {translation(sLocale)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="tw-h-10 tw-text-grey-dark">
                    {!['en', 'default'].includes(String(selectedLocale)) && (
                      <div className="tw-flex tw-items-center">
                        <div><TranslationsSvg/></div>
                        <div className="tw-ml-2">{translation('aiTranslated')}</div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="tw-font-semibold md:tw-mb-5 tw-mb-4">{translation('chooseACurrency')}</div>
                  <div className="row">
                    {orderedCurrencies.map((item, index) => {
                      return (
                        <div className="col-md-4 col-6 md:tw-mb-4 tw-mb-3" key={index}>
                          <div
                            onClick={() => {
                              if (isDisabled) return;
                              setCurrency(item.code);
                              Cookies.set('currency', item.code, {
                                path: '/',
                                domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
                              });
                              router.reload();
                            }}
                            className={classNames(
                              'tw-px-3 tw-py-3 md:tw-py-4 tw-cursor-pointer tw-rounded-lg',
                              {
                                ' tw-bg-white tw-border-brand-navy tw-border-solid tw-border':
                                  currency === item.code,
                                'hover:tw-bg-white': currency !== item.code,
                              }
                            )}
                          >
                            {translation(item.code)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
        </Modal>
      </>
    </>
  );
};

export default LocaleSelector;
